import axiosSetup from "../utils/axiosSetup";
import {
  IWarehouseRecord,
  WarehouseFormData,
  WarehouseUpdateData,
} from "../models/warehouse.model";
import { IWarehouseRawMaterial } from "../models/warehouseRawMaterial.model";
import { IVendor } from "../models/vendor.model";
import {
  FloorInwardRequest,
  WarehouseStock,
} from "../store/slices/floorInwardSlice";
import {
  ManagerFloorInwardRequest,
  PaginatedLotResponse,
} from "../store/slices/managerWarehouseSlice";
import toast from "react-hot-toast";
import ResponseModel from "../models/response.model";

export const createWarehouseApi = async (
  payload: WarehouseFormData,
): Promise<IWarehouseRecord> => {
  try {
    const res = await axiosSetup.post("/warehouse/create/warehouse", payload);
    return res.data.data;
  } catch (error) {
    throw error;
  }
};
export const updateOrderProductMaterialApi = async (payload: {
  orderId: string;
  id?: string;
  qty?: number;
  oldId?: string;
  oldQty?: number;
  type?: string;
  bomId?: string;
  oldBomId?: string | null;
}) => {
  try {
    const res = await axiosSetup.post("/orders/update-order", payload);
    return res.data.data;
  } catch (error) {
    throw error;
  }
};
export const fetchVendorsApi = async ({
  page = 1,
  limit = 10,
  search = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<{
  data: IVendor[];
  totalItems: number;
  totalPages: number;
}> => {
  try {
    const res = await axiosSetup.get("/warehouse/get/vendors", {
      params: { page, limit, search },
    });
    return {
      data: res.data.data.data || [],
      totalItems: res.data.data.totalItems || 0,
      totalPages: res.data.data.totalPages || 0,
    };
  } catch (error) {
    throw error;
  }
};

export const fetchWarehousesApi = async ({
  page = 1,
  limit = 10,
  search = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<{
  data: IWarehouseRecord[];
  totalItems: number;
  totalPages: number;
}> => {
  try {
    const res = await axiosSetup.get("/warehouse/get/warehouses", {
      params: { page, limit, search },
    });
    return {
      data: res.data.data.data || [],
      totalItems: res.data.data.totalItems || 0,
      totalPages: res.data.data.totalPages || 0,
    };
  } catch (error) {
    throw error;
  }
};

export const fetchRawMaterialsApi = async ({
  page = 1,
  limit = 10,
  search = "",
  bom = false,
  sortBy = "",
  sortOrder = "",
  from = "factory",
}: {
  page?: number;
  limit?: number;
  search?: string;
  bom?: boolean;
  sortBy?: string;
  sortOrder?: string;
  from: string;
}): Promise<{
  data: IWarehouseRawMaterial[];
  totalItems: number;
  totalPages: number;
}> => {
  try {
    const res = await axiosSetup.get("/warehouse/rawmaterials", {
      params: { page, limit, search, sortBy, sortOrder, bom, from },
    });
    return {
      data: res.data.data.data || [],
      totalItems: res.data.data.totalItems || 0,
      totalPages: res.data.data.totalPages || 0,
    };
  } catch (error) {
    throw error;
  }
};

export const createVendorApi = async (payload: {
  name: string;
  gstNumber: string;
  contactNumber: string;
  address: string;
}): Promise<IVendor> => {
  try {
    const res = await axiosSetup.post("/warehouse/create/vendor", payload);
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const createRawMaterialApi = async (payload: {
  type: string;
  name: string;
  sku: string;
  unit: string;
  usageType: string;
  approxDeliveryDays: number;
  gst: number;
  hsn: string;
  requireSerialNumber: boolean;
}): Promise<IWarehouseRawMaterial> => {
  try {
    const res = await axiosSetup.post("/warehouse/create/rawmaterial", payload);
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

// export const createFloorInwardRequestApi = async (
//   requests: {
//     productId: string;
//     quantity: number;
//     unit: string;
//     remark?: string;
//   }[],
//   isDirectStoreRequest: boolean = false,
// ): Promise<any> => {
//   try {
//     const path = isDirectStoreRequest
//       ? "/warehouse/store/direct-fullfilled"
//       : "/warehouse/inward/floor";

//     const res = await axiosSetup.post(path, { requests });
//     return res.data.data;
//   } catch (error) {
//     throw error;
//   }
// };

export const createFloorInwardRequestApi = async (
  requests: {
    productId: string;
    quantity: number;
    unit: string;
    remark?: string;
  }[],
  assignee: string,
  productId?: string,
  quantity?: number,
  rawMaterialId?: string,
): Promise<any> => {
  try {
    const path = "/factory/floor/inward-request";

    const res = await axiosSetup.post(path, {
      items: requests,
      requestedBy: assignee,
      productId,
      quantity,
      rawMaterialId,
    });
    return res.data.data;
  } catch (error) {
    throw error;
  }
};
export const fetchPendingFloorInwardsApi = async ({
  pageNumber = 1,
  itemsPerPage = 10,
  search = "",
}: {
  pageNumber?: number;
  itemsPerPage?: number;
  search?: string;
}): Promise<{
  items: FloorInwardRequest[];
  totalPendingInwardRequests: number;
  currentPageNumber: number;
  itemsPerPage: number;
  totalPages: number;
}> => {
  try {
    const res = await axiosSetup.get("/warehouse/floorinward/pending", {
      params: { pageNumber, limit: itemsPerPage, search },
    });
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const fetchUserWarehouseStockApi = async (): Promise<
  WarehouseStock[]
> => {
  try {
    const res = await axiosSetup.get("/warehouse/floorinward/stock");
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const fetchFloorInwardHistoryApi = async ({
  startDate,
  endDate,
  pageNumber = 1,
  itemsPerPage = 10,
  search = "",
}: {
  startDate?: string;
  endDate?: string;
  pageNumber?: number;
  itemsPerPage?: number;
  search?: string;
}): Promise<{
  items: FloorInwardRequest[];
  totalFloorInwardRequests: number;
  currentPageNumber: number;
  itemsPerPage: number;
  totalPages: number;
}> => {
  try {
    const res = await axiosSetup.get("/warehouse/floorinward/history", {
      params: { startDate, endDate, pageNumber, itemsPerPage, search },
    });
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const updateWarehouseApi = async (
  id: string,
  payload: WarehouseUpdateData,
): Promise<IWarehouseRecord> => {
  try {
    const res = await axiosSetup.patch(
      `/warehouse/update/warehouse/${id}`,
      payload,
    );
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const updateVendorApi = async (
  id: string,
  payload: Partial<{
    name: string;
    gstNumber: string;
    contactNumber: string;
    address: string;
  }>,
): Promise<IVendor> => {
  try {
    const res = await axiosSetup.patch(
      `/warehouse/update/vendor/${id}`,
      payload,
    );
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const updateRawMaterialApi = async (
  id: string,
  payload: Partial<{
    type: string;
    name: string;
    description: string;
    sku: string;
    unit: string;
    usageType: string;
    minimumQuantity: number;
    approxDeliveryDays: number;
    gst: number;
    hsn: string;
    requireSerialNumber: boolean;
  }>,
): Promise<IWarehouseRawMaterial> => {
  try {
    const res = await axiosSetup.patch(
      `/warehouse/update/rawmaterial/${id}`,
      payload,
    );
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const fetchManagerPendingRequestsApi = async (
  lotPage: number,
  lotLimit: number,
  search?: string,
): Promise<PaginatedLotResponse> => {
  try {
    const res = await axiosSetup.get("/warehouse/manager/pending", {
      params: { lotPage, lotLimit, search },
    });
    return res.data.data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

export const updateManagerPendingRequestApi = async (
  payloadArray: {
    id: string;
    status: string;
    quantity?: number;
    suggestedQuantity?: number;
    suggestedUnit?: string;
    remark?: string;
  }[],
): Promise<ManagerFloorInwardRequest[]> => {
  try {
    const res = await axiosSetup.patch(
      `/warehouse/manager/update`,
      payloadArray,
    );
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const cancelFloorInwardRequestApi = async (
  requestId: string,
): Promise<FloorInwardRequest> => {
  try {
    const res = await axiosSetup.patch(
      `/warehouse/inward/floor/${requestId}/cancel`,
    );
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const approveFloorInwardChangeRequestApi = async (
  requestId: string,
): Promise<FloorInwardRequest> => {
  try {
    const res = await axiosSetup.patch(
      `/warehouse/inward/floor/${requestId}/approve`,
    );
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const fetchPaginatedFloorStockApi = async ({
  page = 1,
  limit = 10,
  search = "",
  warehouseId = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
  warehouseId?: string;
}): Promise<{
  data: WarehouseStock[];
  totalItems: number;
  totalPages: number;
}> => {
  try {
    const params: any = { page, limit, search, warehouseId };
    const res = await axiosSetup.get("/warehouse/user/stock", { params });

    return {
      data: res.data.data.data || [],
      totalItems: res.data.data.totalItems || 0,
      totalPages: res.data.data.totalPages || 0,
    };
  } catch (error) {
    throw error;
  }
};

export const fetchWarehouseMaterialsApi = async (
  usageType?: string,
): Promise<IWarehouseRawMaterial[]> => {
  try {
    const res = await axiosSetup.get("/warehouse/material/list", {
      params: { type: usageType },
    });
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const fetchWarehouseListApi = async (): Promise<IWarehouseRecord[]> => {
  try {
    const res = await axiosSetup.get("/warehouse/getWarehouseList");
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const createOutwardProductRequestApi = async (
  payload: {
    productId: string;
    warehouseId: string;
    serialNumber: string;
    loggerSerialNumber: string;
    manufacturingSerialNumber: string;
  }[],
) => {
  try {
    const res = await axiosSetup.post(
      "/warehouse/createOutwardProductRequest",
      payload,
    );
    return res.data.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

export const createOutwardMaterialRequestApi = async (
  payload: {
    materialId: string;
    warehouseId: string;
    quantity: number;
    unit: string;
  }[],
) => {
  try {
    const res = await axiosSetup.post(
      "/warehouse/create-outward-material-request",
      payload,
    );
    return res.data.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

export const fetchWarehouseNamesAndIdsApi = async (): Promise<
  { _id: string; name: string }[]
> => {
  try {
    const res = await axiosSetup.get("/warehouse/names-ids");
    return res.data.data;
  } catch (error) {
    throw error;
  }
};

export const fetchStockReductionLogsRawmaterialApi = async (params: {
  search?: string;
  page: number;
  limit?: number;
  rawMaterialId?: string;
  warehouseId?: string;
  productId?: string;
}) => {
  try {
    const response = await axiosSetup.get(
      "/orders/warehouse/stock-reduction-logs",
      {
        params,
      },
    );
    return response.data as ResponseModel;
  } catch (err: any) {
    throw err.response?.data?.message;
  }
};
export const downloadCombinedStockReportApi = async (
  format: "csv" | "xlsx" = "csv",
  date: string,
) => {
  try {
    const res = await axiosSetup.get(
      `dashboard/admin/combined-stock-report?format=${format}&date=${date}`,
      {
        responseType: "blob",
      },
    );

    const blob = new Blob([res.data], {
      type:
        format === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `combined_stock_report_${date}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success(`Report for ${date} downloaded as ${format.toUpperCase()}`);
    return { success: true };
  } catch (err: any) {
    const message = err?.response?.data?.message || "Failed to download report";
    toast.error(message);
    throw new Error(message);
  }
};

export const getProductRawMaterialsApi = async (productId: string) => {
  try {
    const res = await axiosSetup.get(
      `/warehouse/rawmaterials-product/${productId}`,
    );
    return res.data.data.rawMaterials;
  } catch (error) {
    throw error;
  }
};

export const getMaterialsRawMaterialsApi = async (materialId: string) => {
  try {
    const res = await axiosSetup.get(
      `/warehouse/rawmaterials-material/${materialId}`,
    );
    return res.data.data.rawMaterials;
  } catch (error) {
    throw error;
  }
};

export const deleteWarehouseVendorRawmaterialItemApi = async (
  id: string,
  type: "factory" | "vendor" | "material",
) => {
  try {
    const res = await axiosSetup.delete(`/warehouse/delete/${id}`, {
      data: { type },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const fetchStoreInwardHistoryApi = async (
  productId: string,
  page: number,
  limit: number,
) => {
  const response = await axiosSetup.get(
    `warehouse/store-inward/history/${productId}?page=${page}&limit=${limit}`,
  );
  return response.data;
};

export const fetchAdjustableStockApi = async (
  itemId: string,
  warehouseId: string,
  type: string,
) => {
  const response = await axiosSetup.get(`/warehouse/fetch/${itemId}`, {
    params: { warehouseId, type },
  });
  return response.data;
};

export const adjustStockApi = async (payload: {
  itemId: string;
  type: "product" | "material";
  warehouseId: string;
  adjustmentQty: number;
  adjustmentType: "increase" | "decrease";
  unit: string;
}) => {
  const response = await axiosSetup.post("/warehouse/adjust-stock", payload);
  return response.data;
};
